import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { selectAssignmentCostFields, selectTopicCostFields } from '@src/suite/common/store/global-tables/global-tables.selectors';

import { AssignmentsRecordState, DiscoverRecordState, PublicationsRecordState, RundownsRecordState, StoriesRecordState, TopicsRecordState, UsersRecordState } from './records.models';

// Topics
const selectTopicRecordsState = createFeatureSelector<TopicsRecordState>('topicRecords');
export const selectTopicRecordsMap = createSelector(selectTopicRecordsState, state => state?.topics);
export const selectTopicRecords = createSelector(selectTopicRecordsMap, topics => Array.from(topics.values()));
export const selectTopicTemplatesMap = createSelector(selectTopicRecordsState, state => state?.topicTemplates);
export const selectTopicTemplates = createSelector(selectTopicTemplatesMap, topicTemplates => Array.from(topicTemplates?.values()));
export const selectTopicRecordByGuid = (guid: string) => createSelector(selectTopicRecordsMap, topics => topics.get(guid));

// Assignments
const selectAssignmentRecordsState = createFeatureSelector<AssignmentsRecordState>('assignmentRecords');
export const selectAssignmentRecordsMap = createSelector(selectAssignmentRecordsState, selectTopicRecordsMap, selectAssignmentCostFields, (state, topics, fields) => {
    const ret = new Map<string, StorytellingAssignmentRecord>();
    state.assignments.forEach(assignment => {
        assignment.totalCost = fields.reduce((acc, field) => {
            return acc + (assignment[field.ID]?.totalCost || 0);
        }, 0);
        ret.set(assignment.Guid, {
            ...assignment,
            // TODO Calulate strings properly
            topicMetadata: topics.get(assignment.ParentGuid) || null,
        });
    });
    return ret;
});
export const selectAssignments = createSelector(selectAssignmentRecordsMap, assignments => Array.from(assignments.values()));
export const selectAssignmentsByTopic = createSelector(selectAssignmentRecordsState, state => state?.assignmentsByTopic);
export const selectAssignmentRecordByGuid = (guid: string) => createSelector(selectAssignmentRecordsMap, assignments => assignments.get(guid));

// Locations
const selectUserRecordsState = createFeatureSelector<UsersRecordState>('userRecords');
export const selectUserRecordsMap = createSelector(selectUserRecordsState, state => {
    return state.users;
});
export const selectUserRecords = createSelector(selectUserRecordsState, state => {
    return Array.from(state.users?.values()) || [];
});

// Stories
const selectStoryRecordsState = createFeatureSelector<StoriesRecordState>('storyRecords');
export const selectStoryRecordsMap = createSelector(selectStoryRecordsState, selectTopicRecordsMap, (state, topics) => {
    const ret = new Map<string, StorytellingStoryRecord>();
    state.stories.forEach(story => {
        ret.set(story.Guid, {
            ...story,
            // TODO Calculate strings properly
            topicMetadata: topics.get(story.PSTStoryTopicGUID) || topics.get(story.ParentGuid) || null,
        });
    });
    return ret;
});
export const selectStoryRecords = createSelector(selectStoryRecordsMap, stories => Array.from(stories.values()));
export const selectStoryTemplates = createSelector(selectStoryRecordsState, state => Array.from(state?.storyTemplates?.values()));
export const selectStoryTemplatesMap = createSelector(selectStoryRecordsState, state => state?.storyTemplates);
export const selectStoryRecordsByTopic = createSelector(selectStoryRecordsState, state => {
    return state?.storiesByTopic;
});
export const selectStoryRecordByGuid = (guid: string) => createSelector(selectStoryRecordsMap, stories => stories.get(guid));

//Discover Items
const selectDiscoverRecordsState = createFeatureSelector<DiscoverRecordState>('discoverRecords');
export const selectDiscoverRecordsMap = createSelector(selectDiscoverRecordsState, state => {
    const ret = new Map<string, StorytellingRecord>();
    state.discoverItems.forEach(disocverItem => {
        ret.set(disocverItem.Guid, {
            ...disocverItem,
        });
    });
    return ret;
});

export const selectTopicsWithAssignments = createSelector(selectTopicRecords, selectAssignmentsByTopic, selectAssignmentRecordsMap, (topics, assignmentsByTopicMap, assignments) => {
    const ret = new Map<string, StorytellingTopicRecord>();
    topics.forEach(topic => {
        ret.set(topic.Guid, {
            ...topic,
            children: topic?.Sequence?.map(guid => assignments.get(guid)).filter(val => val) || [],
        });
    });
    return ret;
});

export const selectTopicsWithAssignmentsAndStories = createSelector(
    selectTopicRecords,
    selectAssignmentRecordsMap,
    selectStoryRecordsMap,
    selectDiscoverRecordsMap,
    selectTopicCostFields,
    (topics, assignments, stories, disocverItems, fields) => {
        const ret = new Map<string, StorytellingTopicRecord>();
        topics.forEach(topic => {
            topic.totalCost = fields.reduce((acc, field) => {
                return acc + (topic[field.ID]?.totalCost || 0);
            }, 0);
            const storiesForTopic = Array.from(stories)
                .filter(story => {
                    return story[1]?.PSTStoryTopicGUID === topic.Guid && !story[1]?.Path?.isWasteBinPath;
                })
                .map(story => story[1]);
            const children =
                topic?.Sequence?.map(guid => assignments?.get(guid)).filter(val => {
                    topic.totalCost += val?.totalCost || 0;
                    return val;
                }) || [];
            ret.set(topic.Guid, {
                ...topic,
                children: children,
                stories: storiesForTopic,
                LinkedRecords: topic?.Relationships?.data
                    .map(rel => {
                        switch (Number(rel[1])) {
                            case ObjectTypes.DISCOVER_ITEM:
                                return disocverItems.get(rel[0]) || null;
                            default:
                                return null;
                        }
                    })
                    .filter(val => !!val),
            });
        });
        return ret;
    }
);

// TODO: Handle Links

// Rundowns
const selectRundownRecordsState = createFeatureSelector<RundownsRecordState>('rundownRecords');
export const selectRundownRecordsMap = createSelector(selectRundownRecordsState, state => state?.rundowns);
export const selectRundownRecords = createSelector(selectRundownRecordsMap, rundowns => Array.from(rundowns.values()));
export const selectRundownRecordByGuid = (guid: string) => createSelector(selectRundownRecordsMap, rundowns => rundowns.get(guid));

export const selectRundownsWithStories = createSelector(selectRundownRecords, selectStoryRecordsMap, (rundowns, stories) => {
    const ret = new Map<string, StorytellingRundownRecord>();
    rundowns.forEach(rundown => {
        ret.set(rundown.Guid, {
            ...rundown,
            children: rundown?.Sequence?.map(guid => stories.get(guid)).filter(val => val) || [],
        });
    });
    return ret;
});

// Publication Channels
const selectPublicationChannelsState = createFeatureSelector<PublicationsRecordState>('publicationRecords');
export const selectPublicationChannelsArray = createSelector(selectPublicationChannelsState, state => {
    return Array.from(state?.publicationChannels?.values() || []);
});

export const selectPublicationChannelsMap = createSelector(selectPublicationChannelsState, state => {
    return state?.publicationChannels;
});

export const selectPublicationListsMap = createSelector(selectPublicationChannelsState, state => {
    return state?.publicationList;
});

export const selectPublications = createSelector(selectPublicationChannelsState, state => {
    return state?.publications;
});

export const selectRecordByGuid = (guid: string) =>
    createSelector(selectTopicRecordsMap, selectStoryRecordsMap, selectRundownRecordsMap, selectAssignmentRecordsMap, (topicsMap, storiesMap, rundownsMap, assignmentsMap) => {
        return topicsMap.get(guid) || storiesMap.get(guid) || rundownsMap.get(guid) || assignmentsMap.get(guid);
    });
